import BaseAPI from '../BaseAPI';
import { IVFSResponse } from '../types';
import {
  IUsage,
  UpdateSubscriptionResponseBody,
  GetSubscriptionResponseData,
  SetupPaymentsResponse,
} from './types';

/**
 * Subscription API
 * Handles subscription and billing operations
 */
class SubscriptionAPI extends BaseAPI {
  /**
   * Get subscription details
   * @param teamId - Team ID
   */
  async getSubscriptionDetails({
    teamId,
  }: {
    teamId: string;
  }): Promise<IVFSResponse<GetSubscriptionResponseData>> {
    return this.wrapResponse<GetSubscriptionResponseData>({
      promise: this.apiInstance.get(`/v2/billing/subscriptions/${teamId}`),
      errorMessage: 'Failed to get subscription details',
    });
  }

  /**
   * Create or update a subscription
   * @param teamId - Team ID
   * @param totalAmount - Total amount
   * @param plan - Plan name
   * @param term - Subscription term
   * @param promoCode - Optional promo code
   * @param trialLabel - Optional trial label
   * @param metadata - Optional metadata
   */
  async createOrUpdateSubscription({
    teamId,
    totalAmount,
    plan,
    term,
    promoCode,
    trialLabel,
    metadata,
  }: {
    teamId: string;
    totalAmount: number;
    plan: string;
    term: string;
    promoCode?: string;
    trialLabel?: string;
    metadata?: {
      toltReferral?: string;
    };
  }): Promise<IVFSResponse<UpdateSubscriptionResponseBody>> {
    const payload = {
      amount: totalAmount,
      plan,
      term,
      promoCode,
      trialLabel,
      metadata,
    };

    return this.wrapResponse<UpdateSubscriptionResponseBody>({
      promise: this.apiInstance.put(
        `/v2/billing/subscriptions/${teamId}`,
        payload,
      ),
      errorMessage: 'Failed to create or update subscription',
    });
  }

  /**
   * Get subscription upgrade status.
   * @deprecated Since we now collect the payment details before creating the
   * subscription, there is no `incomplete` subscription status, and hence, no
   * need to repeatedly poll this endpoint.
   *
   * This can be deleted if not used elsewhere.
   *
   * @param teamId - Team ID
   */
  async getSubscriptionUpgradeStatus(
    teamId: string,
  ): Promise<IVFSResponse<GetSubscriptionResponseData>> {
    return this.wrapResponse<GetSubscriptionResponseData>({
      promise: this.apiInstance.post(
        `/v2/billing/subscriptions/${teamId}/upgrade/status`,
      ),
      errorMessage: 'Failed to get subscription upgrade status',
    });
  }

  /**
   * Get usage data
   * @param teamId - Team ID
   */
  async getUsage(teamId: string): Promise<IVFSResponse<IUsage>> {
    return this.wrapResponse<IUsage>({
      promise: this.apiInstance.get(
        `/v2/billing/subscriptions/${teamId}/usage`,
      ),
      errorMessage: 'Failed to get usage data',
    });
  }

  /**
   * Set up payment methods
   * @param teamId - Team ID
   * @param metadata - Payment metadata
   */
  async setupPayments({
    teamId,
    metadata,
  }: {
    teamId: string;
    metadata: {
      toltReferral?: string;
    };
  }): Promise<IVFSResponse<SetupPaymentsResponse>> {
    const payload = { metadata };

    return this.wrapResponse<SetupPaymentsResponse>({
      promise: this.apiInstance.post(
        `/v2/billing/subscriptions/${teamId}/setup-payments`,
        payload,
      ),
      errorMessage: 'Failed to set up payments',
    });
  }

  /**
   * End trial immediately and activate paid subscription
   * @param teamId - Team ID
   */
  async endTrialImmediately({
    teamId,
  }: {
    teamId: string;
  }): Promise<IVFSResponse<GetSubscriptionResponseData>> {
    return this.wrapResponse<GetSubscriptionResponseData>({
      promise: this.apiInstance.post(
        `/v2/billing/subscriptions/${teamId}/end-trial`,
      ),
      errorMessage: 'Failed to activate subscription',
    });
  }
}

export default SubscriptionAPI;
